import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import React, { createContext, useCallback, useContext, useEffect } from 'react';
import AuthLoader from '../components/auth/AuthLoader';
import SessionExpiredModal from '../components/auth/SessionExpiredModal';
import { useAuthState } from '../hooks/useAuthState';
import { useAuthEvents } from '../hooks/useAuthEvents';
import { useMembershipCheck } from '../hooks/useMembershipCheck';
import { useObservabilityInit } from '../hooks/useObservabilityInit';
import { useActiveUserTracking } from '../hooks/useActiveUserTracking';
import { usePageViewTracking } from '../hooks/usePageViewTracking';
import { setPostAuthRedirect, handlePostAuthRedirect } from '../utils/authRedirect';
import { configureAmplify } from '../config/amplifyConfig';
import type { AuthContextType, AuthProviderProps, LoggedInUserInfo } from '../types/auth/auth.types';

// Extended AuthContextType to include user role modification methods
interface ExtendedAuthContextType extends AuthContextType {
  updateUserAuthDetails: (updates: Partial<LoggedInUserInfo>, fromModifyAuthContext: boolean) => void;
  resetToDefaultAuthDetails: () => void;
  recheckMembership: () => Promise<void>;
}

const AuthContext = createContext<ExtendedAuthContextType | undefined>(undefined);

// Constants
const SESSION_CHECK_INTERVAL = 2 * 60 * 1000; // Every 2 minutes

/**
 * Custom hook to use the authentication context
 */
export const useAuth = (): ExtendedAuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user,
    error,
    isLoading,
    isAuthInitialized,
    isServicesInitialized,
    sessionExpired,
    notifications,
    checkAuthState,
    updateUserAuthDetails,
    resetToDefaultAuthDetails,
    signIn,
    signOut,
    refreshSession,
    dismissNotification,
    setSessionExpired,
    setUser,
    setError,
    setIsLoading,
    setIsAuthInitialized,
  } = useAuthState();

  // Use custom hook for membership checking and critical data loading
  const { isMembershipCheckComplete, isCriticalDataLoaded, checkMembership, resetMembershipCheck } = useMembershipCheck(
    {
      userAlias: user?.userAlias,
      isAuthInitialized,
      isServicesInitialized,
      onMembershipUpdate: (updates) => {
        updateUserAuthDetails(updates, false);
      },
      onError: (errorMessage) => {
        setError(errorMessage);
      },
    }
  );

  // Initialize observability tools (logger and metrics) after authentication
  const { metricsReady } = useObservabilityInit({
    user,
    isAuthInitialized,
    isMembershipCheckComplete,
  });

  // Track active user sessions in real-time with heartbeat (only after metrics are ready)
  useActiveUserTracking({ user, metricsReady });

  // Track page views and navigation patterns (only after metrics are ready)
  usePageViewTracking({ user, metricsReady });

  // Create a function that resets and rechecks (for delete own record scenario)
  const recheckMembershipFull = useCallback(async (): Promise<void> => {
    resetMembershipCheck();
    await checkMembership();
  }, [resetMembershipCheck, checkMembership]);

  // Handle post-auth redirect after both membership check and critical data are loaded
  useEffect(() => {
    if (isMembershipCheckComplete && isCriticalDataLoaded && user) {
      handlePostAuthRedirect();
    }
  }, [isMembershipCheckComplete, isCriticalDataLoaded, user]);

  // Note: Current user permissions are fetched by useMembershipCheck hook
  // No need to fetch them again here to avoid duplicate API calls

  // Handle authentication events
  useAuthEvents({
    checkAuthState,
    user,
    isAuthInitialized,
    setUser,
    setError,
    setIsLoading,
    setIsAuthInitialized,
    setSessionExpired,
  });

  // Enhanced signIn with redirect handling and Amplify reconfiguration
  const signInWithRedirectHandling = useCallback(async (): Promise<void> => {
    try {
      // Ensure Amplify is properly configured before attempting sign in
      configureAmplify();
      setPostAuthRedirect();
      await signIn();
    } catch (error) {
      console.error('Sign in failed:', error);
      setError('Authentication configuration failed. Please refresh the page.');
    }
  }, [signIn, setError]);

  // Session expiration check
  const checkSessionExpiration = useCallback(async (): Promise<void> => {
    if (!user) return;
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        throw new Error('No valid tokens found');
      }
    } catch (error) {
      if (user?.userAlias) {
        console.warn('Session expired for user:', user.userAlias, error);
        setSessionExpired(true);
        setUser(null);
        setError('Session expired');
        setIsAuthInitialized(false);
      }
    }
  }, [user, setSessionExpired, setUser, setError, setIsAuthInitialized]);

  // Setup periodic session check
  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      void checkSessionExpiration();
    }, SESSION_CHECK_INTERVAL);
    return (): void => clearInterval(intervalId);
  }, [checkSessionExpiration, user]);

  // Show loader until ready
  if (isLoading || !isAuthInitialized || !isServicesInitialized) {
    console.log('AuthProvider loading state:', { isLoading, isAuthInitialized, isServicesInitialized });
    return <AuthLoader />;
  }

  // Immediate redirect for unauthenticated users
  if (isAuthInitialized && !user && !sessionExpired) {
    console.log('No user found, redirecting to SSO...');
    try {
      // Ensure Amplify is properly configured before attempting redirect
      configureAmplify();
      setPostAuthRedirect();
      void signInWithRedirect({ provider: { custom: 'AmazonFederate' } });
    } catch (error) {
      console.error('Failed to configure authentication for redirect:', error);
      setError('Authentication configuration failed. Please refresh the page.');
    }
    return <AuthLoader />;
  }

  // Show loader until membership check is complete
  if (user && !isMembershipCheckComplete) {
    console.log('AuthProvider waiting for membership check completion for user:', user.userAlias);
    return <AuthLoader />;
  }

  // Show loader until critical data is loaded
  if (user && isMembershipCheckComplete && !isCriticalDataLoaded) {
    console.log('AuthProvider waiting for critical data to load for user:', user.userAlias);
    return <AuthLoader />;
  }

  console.log('AuthProvider rendering children with user:', user?.userAlias, {
    isMembershipCheckComplete,
    isCriticalDataLoaded,
  });

  const contextValue: ExtendedAuthContextType = {
    user: user!,
    isAuthenticated: !!user,
    isLoading,
    error,
    sessionExpired,
    notifications,
    signIn: signInWithRedirectHandling,
    signOut,
    refreshSession,
    dismissNotification,
    updateUserAuthDetails,
    resetToDefaultAuthDetails,
    recheckMembership: recheckMembershipFull,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <SessionExpiredModal visible={sessionExpired} />
      {children}
    </AuthContext.Provider>
  );
};
